import { useCallback, useEffect, useMemo } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useFetcher } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import { EDITORIAL_GROUND } from '../config/editorial-ground';
import { ArticleTile } from '@rocco/ui/tile-article/components/ArticleTile';
import {
    TileOverlayWithFollowSelector,
    TileMobileActionsSlot,
} from '~/modules/moodboards/components';
import { buildTileActionProps } from '~/modules/moodboards/utils/tile-action-props';
import { ArticleTileAdminMenu } from '~/modules-rocco/admin/components/ArticleTileAdminMenu';
import { mapArchizeenTileToEditorial } from '../util/map-archizeen-tile-to-editorial';
import {
    HREF_RECENTLY_VIEWED_ARTICLES,
    type loader,
} from '~/routes/remix-api.articles.get-recently-viewed-articles';
import { fetcherIsInit } from '~/utils/fetcherHelper';

interface EditorialRecentlyViewedProps {
    className?: string;
}

/**
 * The reader's recently viewed articles, closing both editorial templates.
 *
 * Composed exactly as the products, projects and professionals browse pages
 * close their grids: a rule, then CarouselSection carrying the heading and its
 * controls on one row above a drag-free strip. All four directories then page
 * and drag identically.
 *
 * Editorial supplies the parts that are its own — the bracketed Gestura title
 * the sections above it use, and the same article tile as the grid — through
 * the component's own title and children rather than a second heading, which
 * would leave CarouselSection's control row standing empty beneath it.
 *
 * Client-fetched, as the legacy section is — recently viewed is per-reader, so
 * it must not be baked into a cacheable document.
 */
export const EditorialRecentlyViewed = ({
    className,
}: EditorialRecentlyViewedProps) => {
    const fetcher = useFetcher<typeof loader>();
    const recentlyViewed = fetcher.data?.recentlyViewedArticles;
    const tracker = useTracker();
    const logTileClick = useCallback(
        (articleId: number) => {
            void tracker.log('ArticleRecentlyViewedClick', {
                url: new URL(window.location.href),
                targetTracker: 'archiproTracker',
                data: { ExtraData: articleId },
            });
        },
        [tracker],
    );

    useEffect(() => {
        if (fetcherIsInit(fetcher)) {
            fetcher.load(HREF_RECENTLY_VIEWED_ARTICLES);
        }
    }, [fetcher]);

    const tiles = useMemo(
        () =>
            (recentlyViewed ?? []).map((article) => ({
                ...mapArchizeenTileToEditorial(article, {
                    variant: 'editorialLarge',
                }),
                // The recently-viewed API maps its tiles with the category
                // list in `label` (the legacy strip shows it); the editorial
                // card's byline wants the author words.
                label: article.authorLabel ?? '',
            })),
        [recentlyViewed],
    );

    if (tiles.length === 0) return null;

    return (
        <section
            aria-label="Recently viewed articles"
            // The editorial paper, said explicitly: this section mounts in
            // the articles layout outside the templates' <main>, so without
            // its own ground it stands as a white band across the page.
            className={cn('flex flex-col', EDITORIAL_GROUND, className)}
        >
            {/* The section rule, on the carousel's own gutters so the two line
                up. Same hairline as EditorialSectionHeader draws. Desktop
                only — the category pages' mobile strips lean on whitespace,
                and this one follows them. */}
            <div className="hidden px-6 md:block md:px-12">
                <hr className="m-0 h-0 border-t border-black/50" />
            </div>
            <CarouselSection
                // Below md the strip is the category pages': their title,
                // their type, their gutter, their card size — so all four
                // directories close the same way on a phone. md+ keeps the
                // bracketed Gestura title of the editorial templates. All of
                // it by breakpoint, never by the JS display mode — that
                // resolves after hydration and lied on the server render.
                title={
                    <>
                        <span className="md:hidden">Recently viewed</span>
                        <span className="hidden md:inline">
                            (Recently Viewed)
                        </span>
                    </>
                }
                titleClassName="m-0 text-6 font-medium tracking-[0.025rem] text-[#181818] md:font-gestura md:text-[2.0625rem] md:leading-[1.25] md:font-normal md:tracking-[-0.0516rem]"
                headerClassName="px-4 md:px-12"
                contentClassName="px-4 md:px-12"
                size="editorialStrip"
                dragFree
            >
                {tiles.map((tile) => (
                    // The grid cell's own chrome, not just its card: admin
                    // menu, the platform action panel, and the mobile `•••`
                    // outside the link so a tap on it can never navigate.
                    <div key={tile.id} className="relative">
                        <ArticleTileAdminMenu article={tile}>
                            <ArticleTile
                                model={tile}
                                aspectRatio="featured"
                                rounded={false}
                                onClick={() => logTileClick(tile.id)}
                                // The grid's half-column title cut below md;
                                // the editorial 24px from md. The standfirst
                                // hides below md in the tile itself.
                                titleClassName="text-[1rem] leading-[1.3rem] md:text-[1.5rem] md:leading-[2rem]"
                                overlay={
                                    <TileOverlayWithFollowSelector
                                        {...buildTileActionProps(tile)}
                                    />
                                }
                            />
                        </ArticleTileAdminMenu>
                        <TileMobileActionsSlot
                            {...buildTileActionProps(tile)}
                        />
                    </div>
                ))}
            </CarouselSection>
        </section>
    );
};
